import { FC, useState } from "react";
import styled from "styled-components";
import {
  Button,
  ButtonVariant,
  ModalHeader,
  Typography,
  TypographyVariant,
} from "../../../../components/ui-kit";
import { useModalManager } from "../../../../context/ModalManager";
import {
  NotificationTypes,
  useNotification,
} from "../../../../hooks/useNotification";
import api from "../../../../util/api";
import { CompanyApiType } from "../../../../util/types";

const Wrapper = styled.div`
  width: 520px;
  @media (max-width: 767px) {
    width: 100%;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px 30px 30px;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 25px;
`;

const CancelButton = styled(Button)`
  margin-right: 10px;
`;

interface MarkTestCompaniesModalProps {
  selectedCompanies: CompanyApiType[];
  onClearSelection: () => void;
  /** Must return the query refetch promise so the list updates after bulk action. */
  onRefresh: () => Promise<unknown>;
  /** When false, clears the test-company flag (`isTestCompany: false`). */
  isTestCompany?: boolean;
}

export const MarkTestCompaniesModal: FC<MarkTestCompaniesModalProps> = ({
  selectedCompanies,
  onClearSelection,
  onRefresh,
  isTestCompany = true,
}) => {
  const [loading, setLoading] = useState(false);
  const { showNotification } = useNotification();
  const { removeLastModal } = useModalManager();

  const ids = selectedCompanies.map((c) => c._id);
  const count = ids.length;

  const handleConfirm = async () => {
    try {
      setLoading(true);
      await api.bulkSetCompaniesIsTestCompany(ids, isTestCompany);
      await onRefresh();
      onClearSelection();
      showNotification(
        isTestCompany
          ? count === 1
            ? "Company marked as test company"
            : `${count} companies marked as test companies`
          : count === 1
          ? "Test company flag removed"
          : `Test company flag removed for ${count} companies`,
        NotificationTypes.SUCCESS
      );
      removeLastModal();
    } catch {
      showNotification(
        isTestCompany
          ? "Could not mark companies as test. You may need super admin access."
          : "Could not update test company flag. You may need super admin access.",
        NotificationTypes.WARNING
      );
    } finally {
      setLoading(false);
    }
  };

  const title = isTestCompany ? "Mark as test company" : "Unmark test company";
  const bodyText = isTestCompany
    ? count === 1
      ? "This company will be flagged as a test company for admin purposes."
      : `${count} companies will be flagged as test companies for admin purposes.`
    : count === 1
    ? "This company will no longer be flagged as a test company."
    : `${count} companies will no longer be flagged as test companies.`;
  const suffix =
    " You can still use them as normal; this flag is mainly for internal filtering and reporting.";

  return (
    <Wrapper>
      <ModalHeader title={title} handleClose={removeLastModal} />
      <Content>
        <Typography variant={TypographyVariant.BODY2}>
          {bodyText}
          {isTestCompany ? suffix : ""}
        </Typography>
        <Buttons>
          <CancelButton onClick={removeLastModal} variant={ButtonVariant.TEXT}>
            Cancel
          </CancelButton>
          <Button
            variant={ButtonVariant.CONTAINED}
            onClick={() => void handleConfirm()}
            loading={loading}
          >
            Confirm
          </Button>
        </Buttons>
      </Content>
    </Wrapper>
  );
};
